import { useEffect, useState } from 'react';
import styled from 'styled-components';
import { useMutation, useQueryClient } from '@tanstack/react-query';

import { useTranslation } from '@hooks';
import { CartServices } from '@services';
import { useAccount } from '@context/AccountContext';
import { useNotification } from '@context/NotificationContext';
import { useCart } from '@context/CartContext';
import { Caption, Text2 } from '@components/styles/Typography';
import { NewButton } from '@components/ui';

const AddCoupon = ({ couponName, disabled }) => {
    const { t } = useTranslation();
    const { userData } = useAccount();
    const { open } = useNotification();
    const { basketHash, currentUser } = useCart();
    const [inputOption, setInputOption] = useState('type');
    const [couponCode, setCouponCode] = useState('');
    const queryClient = useQueryClient();

    const addCouponMutation = useMutation({
        mutationFn: CartServices.addCoupon,
        onSuccess: (data) => {
            if (data?.success) {
                queryClient.invalidateQueries(['getCart']);
            }
            if (data?.errorType) {
                open({
                    type: 'error',
                    description: data.errorType,
                });
            }
        },
    });
    const discardCouponMutation = useMutation({
        mutationFn: CartServices.discardCoupon,
        onSuccess: () => {
            queryClient.invalidateQueries(['getCart']);
        },
    });
    const { available_coupons: avalaibleCoupons } = userData?.data?.data || {
        available_coupons: [],
    };

    const handleAddCoupon = () => {
        addCouponMutation.mutate({
            basketHash,
            token: currentUser,
            couponCode,
        });
    };

    const handleChange = (e) => {
        setCouponCode(e.target.value.toUpperCase());
    };

    const handleDiscard = () => {
        discardCouponMutation.mutate({
            basketHash,
            token: currentUser,
        });
    };

    useEffect(() => {
        if (avalaibleCoupons.length > 0) {
            setInputOption('select');
        }
    }, [avalaibleCoupons]);

    useEffect(() => {
        setCouponCode('');
    }, [inputOption]);

    return (
        <AddCouponWrapper>
            <Text2>{t('cartEnterDiscountCode')}</Text2>
            {!couponName ? (
                <>
                    <CouponContent>
                        {inputOption === 'select' ? (
                            <CouponSelect onChange={handleChange}>
                                <option disabled selected>
                                    {t('selectCoupon')}
                                </option>
                                {avalaibleCoupons.map((coupon) => (
                                    <option
                                        key={coupon.id}
                                        value={coupon.coupon}
                                    >
                                        {coupon.campaign.name}
                                    </option>
                                ))}
                            </CouponSelect>
                        ) : (
                            <CouponInput
                                placeholder={t('enterCoupon')}
                                value={couponCode}
                                onChange={handleChange}
                                data-testid="coupon-input"
                            />
                        )}
                        <NewButton
                            type="primary"
                            size="small"
                            handleClick={handleAddCoupon}
                            data-testid="add-coupon-button"
                            disabled={disabled}
                        >
                            <Caption color="bgGray10">{t('add')}</Caption>
                        </NewButton>
                    </CouponContent>
                    {inputOption === 'select' ? (
                        <ChangeButton onClick={() => setInputOption('type')}>
                            {t('cartTypeDiscountCode')}
                        </ChangeButton>
                    ) : (
                        // eslint-disable-next-line react/jsx-no-useless-fragment
                        <>
                            {avalaibleCoupons.length > 0 && (
                                <ChangeButton
                                    onClick={() => setInputOption('select')}
                                >
                                    {t('cartChooseDiscountCode')}
                                </ChangeButton>
                            )}
                        </>
                    )}
                </>
            ) : (
                <AddedCoupon>
                    <span>{couponName}</span>
                    <NewButton
                        type="secondary"
                        size="small"
                        handleClick={handleDiscard}
                        content={t('delete')}
                    />
                </AddedCoupon>
            )}
        </AddCouponWrapper>
    );
};

const AddCouponWrapper = styled.div`
    display: flex;
    flex-direction: column;
    justify-content: center;
    width: 260px;
    height: 105px;
    background-color: ${({ theme }) => theme.colors.tertiaryVariant};
    padding: 15px;
    div {
        display: flex;
        justify-content: space-between;
        align-items: center;
        height: 50px;
    }
    h4,
    p {
        margin: 0;
        cursor: pointer;
    }
    @media (max-width: 1024px) {
        width: 100%;
        height: auto;
        margin-top: 20px;
        background: ${({ theme }) => theme.colors.secondary};
        padding: 8px 15px;
        div {
            padding: 0;
        }
    }
`;
const CouponContent = styled.div`
    input {
        ::placeholder {
            font-family: var(--futura-font);
            font-weight: ${({ theme }) => theme.fontWeights.regular};
            font-size: ${({ theme }) => theme.sizes.xs};
            letter-spacing: 1px;
        }
    }
`;
const CouponInput = styled.input`
    height: 32px;
    padding: 6px 8px;
    border: ${({ theme }) => `1px solid ${theme.colors.bgGray50}`};
    background: ${({ theme }) => theme.colors.secondary};
    margin-right: 10px;
    outline: none;
    text-transform: uppercase;

    @media (max-width: 1024px) {
        width: 100%;
        background: ${({ theme }) => theme.colors.tertiaryVariant};
        border: none;
    }
`;
const CouponSelect = styled.select`
    height: 34px;
    padding: 6px 8px;
    border-radius: 3px;
    border: ${({ theme }) => `1px solid ${theme.colors.bgGray50}`};
    background: ${({ theme }) => theme.colors.secondary};
    margin-right: 5px;
    outline: none;
    width: 155px;
    color: ${({ theme }) => theme.colors.primary};

    @media (max-width: 1024px) {
        width: 100%;
    }
`;
const ChangeButton = styled.button`
    border: none;
    background-color: transparent;
    color: ${({ theme }) => theme.colors.primary};
    text-align: left;
    padding: 0;
    cursor: pointer;
`;
const AddedCoupon = styled.div`
    span {
        border: ${({ theme }) => `1px solid ${theme.colors.primary}`};
        padding: 5px 10px;
        background-color: ${({ theme }) => theme.colors.secondary};
        width: 70%;
        max-width: 70vh;
        white-space: nowrap;
        overflow: hidden;
        text-overflow: ellipsis;
        display: inline-block;
    }
    @media (max-width: 1024px) {
        max-width: 90vw;
    }
`;
export default AddCoupon;
